import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { adminAPI, subscriptionAPI } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Eye, Users, Crown, Gift, Activity,
  BarChart3, TrendingUp,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const { data } = await subscriptionAPI.status();
      if (!data.is_admin) {
        toast.error("Admin access required");
        navigate("/");
        return;
      }
      await loadStats();
    } catch {
      toast.error("Access denied");
      navigate("/");
    }
  };

  const loadStats = async () => {
    try {
      const { data } = await adminAPI.stats();
      setStats(data);
    } catch {
      toast.error("Failed to load admin stats");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) return null;

  const { page_views, users, promo_codes } = stats;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Admin Dashboard
            </h1>
            <p className="text-xs text-muted-foreground">App analytics & user management</p>
          </div>
          <Button variant="outline" size="sm" className="ml-auto" onClick={loadStats} data-testid="refresh-stats-btn">
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={<Eye className="w-5 h-5 text-blue-500" />}
            label="Page Views (Today)"
            value={page_views.today}
            sub={`${page_views.month} this month`}
            testId="stat-views-today"
          />
          <StatCard
            icon={<Users className="w-5 h-5 text-emerald-500" />}
            label="Total Users"
            value={users.total}
            sub={`${users.active_7d} active this week`}
            testId="stat-total-users"
          />
          <StatCard
            icon={<Crown className="w-5 h-5 text-amber-500" />}
            label="Pro Users"
            value={users.pro}
            sub={`${users.free} free`}
            testId="stat-pro-users"
          />
          <StatCard
            icon={<Gift className="w-5 h-5 text-purple-500" />}
            label="Promos Redeemed"
            value={promo_codes.redeemed}
            sub={`${promo_codes.available} available`}
            testId="stat-promos"
          />
        </div>

        {/* Row 2: Active Users + Pro vs Free */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="w-4 h-4" /> Active Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold" data-testid="active-7d">{users.active_7d}</p>
                  <p className="text-xs text-muted-foreground">Last 7 days</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/50">
                  <p className="text-2xl font-bold" data-testid="active-30d">{users.active_30d}</p>
                  <p className="text-xs text-muted-foreground">Last 30 days</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Crown className="w-4 h-4" /> Pro vs Free Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <BarSegment label="Pro" value={users.pro} total={users.total} color="bg-amber-500" />
                <BarSegment label="Free" value={users.free} total={users.total} color="bg-zinc-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Page Views Chart */}
        {page_views.daily_chart.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Daily Page Views (Last 14 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={page_views.daily_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="views" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sign-ups Chart */}
        {users.signups_chart.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4" /> User Sign-ups (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={users.signups_chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="free" fill="#94a3b8" name="Free" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="pro" fill="#f59e0b" name="Pro" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Pages */}
        {page_views.top_pages.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Eye className="w-4 h-4" /> Top Pages
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {page_views.top_pages.map((p, i) => (
                  <div key={p.page} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/30">
                    <span className="text-sm">
                      <span className="text-muted-foreground mr-2">{i + 1}.</span>
                      {p.page}
                    </span>
                    <Badge variant="secondary" className="text-xs">{p.views} views</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Promo Code Redemptions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Gift className="w-4 h-4" /> Promo Code Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                {promo_codes.redeemed} redeemed
              </span>
              <span className="px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 font-medium">
                {promo_codes.available} available
              </span>
              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-600 font-medium">
                {promo_codes.total} total
              </span>
            </div>
            {promo_codes.redeemed_list.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium mb-1">Redeemed Codes:</p>
                {promo_codes.redeemed_list.map((r) => (
                  <div key={r.code} className="flex items-center justify-between py-2 px-3 rounded bg-muted/30 text-sm">
                    <div>
                      <span className="font-mono font-medium">{r.code}</span>
                      <span className="text-muted-foreground ml-3">{r.user_name} ({r.user_email})</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {r.redeemed_at ? new Date(r.redeemed_at).toLocaleDateString() : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No codes redeemed yet.</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          {icon}
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
        </div>
        <p className="text-2xl font-bold">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function BarSegment({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span>{label}</span>
        <span className="font-medium">{value} ({pct}%)</span>
      </div>
      <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
